<template>
	<SgNotice
		v-if="conflictMessage"
		severity="warning"
		:message="conflictMessage"
	/>
</template>

<script setup lang="ts">
	import { computed } from "vue";
	import { TaskType } from "seedgreen-shared/dtos/TaskTypeConfig";
	import { PlantMethod } from "seedgreen-shared/dtos/PlantingDTO";
	import { useDefaultPlantingsQuery } from "@/composables/useDefaultPlantingsQuery";
	import SgNotice from "@/components/ui/SgNotice.vue";

	interface Props {
		selectedType: TaskType;
		selectedPlantingIds: number[];
	}

	const props = defineProps<Props>();

	const { plantings } = useDefaultPlantingsQuery();

	// Check for plant method conflicts with selected task type
	const conflictMessage = computed(() => {
		if (!plantings.value || props.selectedPlantingIds.length === 0) {
			return null;
		}

		const selectedPlantings = props.selectedPlantingIds
			.map((id) => plantings.value?.find((p) => p.id === id))
			.filter((p) => p !== undefined);

		if (selectedPlantings.length === 0) return null;

		// Check for conflicts
		if (props.selectedType === TaskType.Planting) {
			// Planting (Direct Seed) task on Transplant plantings
			const transplantPlantings = selectedPlantings.filter(
				(p) => p.plantMethod === PlantMethod.Transplant,
			);
			if (transplantPlantings.length > 0) {
				return `You are requesting a Direct Seed task for ${transplantPlantings.length} transplant planting${transplantPlantings.length > 1 ? "s" : ""}.`;
			}
		} else if (props.selectedType === TaskType.Transplanting) {
			// Transplanting task on Seed plantings
			const seedPlantings = selectedPlantings.filter(
				(p) => p.plantMethod === PlantMethod.Seed,
			);
			if (seedPlantings.length > 0) {
				return `You are requesting a Transplanting task for ${seedPlantings.length} direct seed planting${seedPlantings.length > 1 ? "s" : ""}.`;
			}
		} else if (props.selectedType === TaskType.Thinning) {
			// Thinning task on Transplant plantings
			const transplantPlantings = selectedPlantings.filter(
				(p) => p.plantMethod === PlantMethod.Transplant,
			);
			if (transplantPlantings.length > 0) {
				return `You are requesting a Thinning task for ${transplantPlantings.length} transplant planting${transplantPlantings.length > 1 ? "s" : ""}.`;
			}
		}

		return null;
	});
</script>
